import React, { useState, useEffect } from 'react';
import { personalNotes } from '../api';
import CoachCommentThread from './CoachCommentThread';
import { htmlToPlainText } from '../utils/textUtils';
import './CoachSharedPersonalNotesSection.css';

/**
 * CoachSharedPersonalNotesSection - Display personal notes a player has shared with this coach
 * Shows notes with read-only content and allows coach to add comments
 */
const CoachSharedPersonalNotesSection = ({ playerId, playerName, currentCoachId }) => {
  const [sharedNotes, setSharedNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedNoteId, setExpandedNoteId] = useState(null);

  useEffect(() => {
    if (playerId) {
      loadSharedNotes();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerId]);

  const loadSharedNotes = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await personalNotes.getSharedNotes(playerId);
      setSharedNotes(response.data.notes || []);
    } catch (err) {
      console.error('❌ Error loading shared personal notes:', err);
      setError('Failed to load shared personal notes');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getCategoryIcon = (category) => {
    const categoryMap = {
      reflection: { icon: '💭', label: 'Reflection' },
      analysis: { icon: '📊', label: 'Analysis' },
      progress: { icon: '📈', label: 'Progress' },
      personal: { icon: '📝', label: 'Personal' }
    };
    return categoryMap[category] || { icon: '📌', label: 'Note' };
  };

  if (error) {
    return (
      <div className="coach-shared-notes-section">
        <div className="shared-notes-error">
          ❌ {error}
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="coach-shared-notes-section">
        <div className="shared-notes-loading">Loading shared personal notes...</div>
      </div>
    );
  }

  if (sharedNotes.length === 0) {
    return null;
  }

  return (
    <div className="coach-shared-notes-section">
      <div className="shared-notes-header">
        <h3>📖 Personal Notes Shared With You</h3>
        <p className="shared-notes-subtext">
          {sharedNotes.length} note{sharedNotes.length !== 1 ? 's' : ''} from {playerName}
        </p>
      </div>

      <div className="shared-notes-list">
        {sharedNotes.map(note => (
          <div key={note._id} className="shared-note-card">
            {/* Note Header */}
            <div className="shared-note-header">
              <div className="shared-note-meta">
                <div className="shared-note-title-section">
                  {note.title && (
                    <h4 className="shared-note-title">{note.title}</h4>
                  )}
                  {note.category && (
                    <span className="shared-note-category">
                      {getCategoryIcon(note.category).icon} {getCategoryIcon(note.category).label}
                    </span>
                  )}
                </div>
                <span className="shared-note-date">
                  {formatDate(note.createdAt)}
                </span>
              </div>
              <button
                className="btn-expand-note"
                onClick={() => setExpandedNoteId(expandedNoteId === note._id ? null : note._id)}
                title={expandedNoteId === note._id ? 'Collapse' : 'Expand'}
              >
                {expandedNoteId === note._id ? '▼' : '▶'}
              </button>
            </div>

            {/* Note Content - Expandable */}
            {expandedNoteId === note._id && (
              <div className="shared-note-content">
                <div className="shared-note-text">
                  {htmlToPlainText(note.content)}
                </div>

                {/* Comments Thread */}
                <div className="shared-note-comments">
                  <CoachCommentThread
                    playerId={playerId}
                    noteId={note._id}
                    comments={
                      note.accessGrants && note.accessGrants.length > 0
                        ? (note.accessGrants.find(g =>
                            String(g.coachId) === String(currentCoachId) && !g.revokedAt
                          ) || {}).comments || []
                        : []
                    }
                    onCommentAdded={() => loadSharedNotes()}
                    currentCoachId={currentCoachId}
                  />
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default CoachSharedPersonalNotesSection;
